import { useState, useCallback } from 'react';

interface IColumnPaginationOptions {
  /** Cantidad inicial de elementos a mostrar (default: 10) */
  initialCount?: number;
  /** Cantidad de elementos a agregar en cada carga (default: 10) */
  increment?: number;
  /** Delay en milisegundos para simular carga (default: 1000) */
  loadDelay?: number;
}

/**
 * Hook para manejar la paginación de columnas tipo Trello.
 * Gestiona el conteo de elementos visibles y el estado de carga.
 * @param options - Opciones de configuración del hook
 * @returns Objeto con el estado y funciones para manejar la paginación
 */
const useColumnPagination = (options: IColumnPaginationOptions = {}) => {
  const { initialCount = 10, increment = 10, loadDelay = 1000 } = options;

  const [visibleCount, setVisibleCount] = useState(initialCount);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  /**
   * Incrementa el conteo de elementos visibles con un delay de carga.
   */
  const handleLoadMore = useCallback(() => {
    if (isLoadingMore) return;
    setIsLoadingMore(true);
    setTimeout(() => {
      setVisibleCount(prev => prev + increment);
      setIsLoadingMore(false);
    }, loadDelay);
  }, [isLoadingMore, increment, loadDelay]);

  /**
   * Verifica si hay más elementos para cargar.
   * @param totalItems - Cantidad total de elementos
   * @returns true si hay más elementos por mostrar
   */
  const hasMoreItems = useCallback(
    (totalItems: number) => visibleCount < totalItems,
    [visibleCount]
  );

  return { visibleCount, isLoadingMore, handleLoadMore, hasMoreItems };
};

export default useColumnPagination;

